// Read-only CapEx Financials lens. Project amounts and totals are already USD.
// Mounted only when the client policy gate passes; unmount drops all data.
import React, { useEffect, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { ProjectsTableScroll } from "../ProjectsScrollLayout.jsx";
import {
  getHubFinancials, exportHubFinancials, projectIdsKey, HUB_FINANCIAL_FIELDS,
} from "../../../data/projectHubClient.js";

const LABELS = {
  budget: "Budget", spendToDate: "Spend-to-date", forecast: "Forecast", variance: "Variance",
};

const numFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export function formatHubAmount(value) {
  if (value === null || value === undefined) return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  if (n === 0) return "0";
  return numFmt.format(n);
}

const TH = { padding: "9px 12px", fontSize: T.fsXxs, fontWeight: T.fwBold, color: "var(--v2-mid)", letterSpacing: ".07em", textTransform: "uppercase", background: "var(--v2-tint)", borderBottom: "1px solid var(--v2-line)", whiteSpace: "nowrap", textAlign: "right" };
const TD = { padding: "9px 12px", fontSize: T.fsSm, textAlign: "right", fontVariantNumeric: "tabular-nums", borderBottom: "1px solid var(--v2-hair)", whiteSpace: "nowrap" };

export function ProjectsFinancials({ projectIds, canExport, refreshToken = 0, onCountChange }) {
  const idsKey = projectIdsKey(projectIds);
  const [state, setState] = useState({ key: null, data: null, error: null, loading: false });
  const [exportState, setExportState] = useState({ busy: false, error: null });
  const [reloadTick, setReloadTick] = useState(0);
  const exportContext = useRef({ mounted: true, idsKey, canExport });
  exportContext.current.idsKey = idsKey;
  exportContext.current.canExport = canExport;
  useEffect(() => {
    const context = exportContext.current;
    context.mounted = true;
    return () => { context.mounted = false; };
  }, []);

  useEffect(() => {
    const ids = idsKey ? idsKey.split(",") : [];
    let cancelled = false;
    setState({ key: idsKey, data: null, error: null, loading: ids.length > 0 });
    setExportState({ busy: false, error: null });
    if (ids.length === 0) return undefined;
    Promise.resolve()
      .then(() => getHubFinancials(ids))
      .then((data) => { if (!cancelled) setState({ key: idsKey, data, error: null, loading: false }); })
      .catch((err) => { if (!cancelled) setState({ key: idsKey, data: null, error: err?.message || "Could not load financials.", loading: false }); });
    return () => { cancelled = true; };
  }, [idsKey, reloadTick, refreshToken]);

  const data = state.key === idsKey ? state.data : null;
  const countCallback = useRef(onCountChange);
  countCallback.current = onCountChange;
  const renderedCount = Array.isArray(data?.rows) ? data.rows.length : 0;
  useEffect(() => { countCallback.current?.(renderedCount); }, [renderedCount]);
  exportContext.current.snapshotId = data?.snapshotId;
  const exportReady = Boolean(canExport && data?.snapshotId && state.key === idsKey);

  async function handleExport() {
    if (!exportReady) return;
    setExportState({ busy: true, error: null });
    try {
      const blob = await exportHubFinancials(idsKey.split(","), data.snapshotId);
      if (!exportContext.current.mounted || !exportContext.current.canExport || exportContext.current.idsKey !== idsKey
        || exportContext.current.snapshotId !== data.snapshotId) return;
      if (typeof URL?.createObjectURL !== "function") throw new Error("This browser cannot download the workbook.");
      {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `project-financials-${data.snapshotId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
      }
      setExportState({ busy: false, error: null });
    } catch (err) {
      if (!exportContext.current.mounted || exportContext.current.idsKey !== idsKey) return;
      setExportState({ busy: false, error: err?.message || "Export failed." });
    }
  }

  return (
    <div data-testid="projects-financials" style={{ border: "1px solid var(--v2-line)", borderRadius: 10, background: T.card }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid var(--v2-hair)", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Financials · read-only</div>
          <div data-testid="projects-financials-scope" style={{ fontSize: T.fsXs, color: T.mid }}>
            {data?.scope || `Active projects in current view (${projectIds.length}). All amounts USD, ex tax.`}
          </div>
          <div data-testid="fin-source-caption" style={{ fontSize: T.fsXs, color: T.mid }}>
            {data?.sourceCaption || "Variance = Budget minus Forecast. Source: CapEx 2026 Working Doc."}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setReloadTick(value => value + 1)} style={{ border: `1px solid ${T.border}`, background: T.white, color: T.dark, borderRadius: 8, padding: "6px 12px", fontSize: T.fsSm, cursor: "pointer" }}>Refresh data</button>
        {canExport ? (
          <button
            type="button"
            data-testid="projects-financials-export"
            disabled={!exportReady || exportState.busy}
            onClick={handleExport}
            style={{ border: `1px solid ${T.border}`, background: T.white, color: exportReady ? T.dark : T.light, borderRadius: 8, padding: "6px 12px", fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: exportReady ? "pointer" : "not-allowed" }}
          >
            {exportState.busy ? "Preparing…" : "Download .xlsx"}
          </button>
        ) : null}
      </div>
      {exportState.error && (
        <div role="alert" data-testid="projects-financials-export-error" style={{ padding: "8px 14px", color: T.red, fontSize: T.fsSm, background: T.redSoft }}>{exportState.error}</div>
      )}
      {projectIds.length === 0 ? (
        <div data-testid="projects-financials-empty" style={{ padding: 40, textAlign: "center", color: T.mid, fontSize: T.fsSm }}>No active projects in the current view.</div>
      ) : state.error && state.key === idsKey ? (
        <div role="alert" data-testid="projects-financials-error" style={{ padding: 24, color: T.red, fontSize: T.fsSm }}>
          {state.error}{" "}
          <button type="button" data-testid="projects-financials-retry" onClick={() => setReloadTick((n) => n + 1)} style={{ marginLeft: 8, border: `1px solid ${T.border}`, background: T.white, borderRadius: 6, padding: "3px 10px", cursor: "pointer", color: T.dark }}>Retry</button>
        </div>
      ) : !data ? (
        <div data-testid="projects-financials-loading" style={{ padding: 14 }}>
          {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 14, margin: "10px 0", borderRadius: 4, background: "var(--v2-tint)", width: `${92 - i * 9}%` }} />)}
        </div>
      ) : (
        <FinancialsTable data={data} />
      )}
    </div>
  );
}

function FinancialsTable({ data }) {
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const totals = data.totals || {};
  return (
    <ProjectsTableScroll testId="projects-financials-scroll">
      <table data-testid="projects-financials-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: "left" }}>Project</th>
            {HUB_FINANCIAL_FIELDS.map((f) => <th key={f} style={TH}>{LABELS[f]}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const issues = r.issues || {};
            return (
              <tr key={r.id} data-testid={`fin-row-${r.id}`}>
                <td style={{ ...TD, textAlign: "left" }}>
                  <span style={{ fontWeight: T.fwSemibold, color: T.dark }}>{r.name}</span>
                </td>
                {HUB_FINANCIAL_FIELDS.map((f) => {
                  const v = r[f];
                  const neg = f === "variance" && Number(v) < 0;
                  return (
                    <td key={f} data-testid={`fin-${f}-${r.id}`} title={issues[f] || undefined} style={{ ...TD, color: v == null ? T.light : neg ? T.red : T.dark }}>
                      {r.financialsTracked === false ? "n/a" : formatHubAmount(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {data.coverageCaption && <tr><td data-testid="fin-tracking-caption" colSpan={5} style={{ ...TD, textAlign: "left", color: T.mid }}>{data.coverageCaption}</td></tr>}
          <tr data-testid="fin-totals">
            <td style={{ ...TD, textAlign: "left", fontWeight: T.fwBold, color: T.dark, borderBottom: "none", background: "var(--v2-tint)" }}>
              Portfolio total (USD)
            </td>
            {HUB_FINANCIAL_FIELDS.map((f) => {
              const t = totals[f] || { value: null, covered: 0, total: rows.length };
              const value = t.total > 0 && t.covered === t.total ? t.value : null;
              return (
                <td key={f} data-testid={`fin-total-${f}`} style={{ ...TD, fontWeight: T.fwBold, borderBottom: "none", background: "var(--v2-tint)", color: t.value == null ? T.light : Number(t.value) < 0 && f === "variance" ? T.red : T.dark }}>
                  {formatHubAmount(value)}
                  <div data-testid={`fin-coverage-${f}`} style={{ fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwSemibold }}>{t.covered} of {t.total} projects{t.covered < t.total ? " · incomplete" : ""}</div>
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </ProjectsTableScroll>
  );
}
